import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar, Box, Typography, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Divider, Paper, Stack, Alert, Grid, IconButton,
  InputAdornment, Tooltip, CircularProgress, alpha,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  Add, Send, ContentCopy, Search, CheckCircle, PersonSearch, OpenInNew, Close,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import api from '../../services/api';

type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
type TicketSource = 'complaint' | 'admin' | 'app';

interface TicketCustomer {
  _id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

interface TicketHistory {
  action: 'created' | 'status' | 'priority' | 'assigned' | 'unassigned' | 'reply' | 'resolution';
  from: string | null;
  to: string | null;
  at: string;
  byType: string | null;
  by: { _id: string; name: string | null } | null;
}

interface Ticket {
  _id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  category: string;
  priority: TicketPriority;
  status: TicketStatus;
  source?: TicketSource;
  userId: string | TicketCustomer | null;
  customer?: TicketCustomer | null;
  assignedTo: { _id?: string; firstName?: string; lastName?: string; email?: string; phone?: string } | null;
  resolution?: string | null;
  messages: Array<{ senderId: string; senderType: string; message: string; sentAt: string }>;
  history?: TicketHistory[];
  slaDeadline?: string | null;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  createdAt: string;
}

interface Agent {
  _id: string;
  name: string;
  userType?: string;
}

interface Category {
  name: string;
  complaint?: string;
}

const STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES: TicketPriority[] = ['low', 'medium', 'high', 'urgent'];

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_customer: 'Waiting on Customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_COLOR: Record<TicketStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  open: 'info',
  in_progress: 'warning',
  waiting_customer: 'default',
  resolved: 'success',
  closed: 'error',
};

const PRIORITY_LABEL: Record<TicketPriority, string> = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const PRIORITY_COLOR: Record<TicketPriority, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
};

const SOURCE_LABEL: Record<TicketSource, string> = {
  complaint: 'Complaint (app)',
  admin: 'Created by admin',
  app: 'Support request (app)',
};

const ADMIN_SENDER_TYPES = new Set(['admin', 'staff', 'super_admin', 'client_admin', 'employee', 'tenant_admin']);
const DATETIME_FORMAT = 'MM/DD/YYYY hh:mm A';
const DATETIME_FULL_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
const PAGE_SIZE = 20;
const MOBILE_PATTERN = /^[6-9]\d{9}$/;
const SUBJECT_MIN = 5;
const SUBJECT_MAX = 200;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 5000;

const selectProps = { InputLabelProps: { shrink: true }, SelectProps: { displayEmpty: true } };

function titleCase(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmt(value?: string | null, full = false) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format(full ? DATETIME_FULL_FORMAT : DATETIME_FORMAT) : '—';
}

function personName(p?: { firstName?: string; lastName?: string; email?: string; phone?: string } | null) {
  if (!p) return '';
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.email || p.phone || '';
}

function ticketCustomer(t: Ticket): TicketCustomer | null {
  if (t.customer) return t.customer;
  return t.userId && typeof t.userId === 'object' ? t.userId : null;
}

function customerLabel(t: Ticket) {
  const c = ticketCustomer(t);
  if (!c) return '—';
  const name = personName(c) || 'Unnamed customer';
  return c.phone ? `${name} (${c.phone})` : name;
}

/** Accepts "+91 98765 43210", "09876543210" etc. and returns the 10-digit mobile. */
function normalizeMobile(raw: string) {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

function apiMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { message?: string; errors?: Array<{ msg?: string }> } } })?.response?.data;
  return data?.errors?.[0]?.msg || data?.message || fallback;
}

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 130, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>{children}</Box>
    </Stack>
  );
}

function historyText(h: TicketHistory) {
  const status = (v: string | null) => (v ? STATUS_LABEL[v as TicketStatus] || titleCase(v) : '—');
  const priority = (v: string | null) => (v ? PRIORITY_LABEL[v as TicketPriority] || titleCase(v) : '—');
  switch (h.action) {
    case 'created': return 'Ticket created';
    case 'status': return `Status: ${status(h.from)} → ${status(h.to)}`;
    case 'priority': return `Priority: ${priority(h.from)} → ${priority(h.to)}`;
    case 'assigned': return h.from ? `Reassigned from ${h.from} to ${h.to || 'agent'}` : `Assigned to ${h.to || 'agent'}`;
    case 'unassigned': return `Unassigned${h.from ? ` (was ${h.from})` : ''}`;
    case 'reply': return 'Reply added';
    case 'resolution': return 'Resolution notes updated';
    default: return titleCase(String(h.action));
  }
}

function CreateTicketDialog({
  open, onClose, onCreated, categories, agents, categoryLabel,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (ticket: Ticket) => void;
  categories: Category[];
  agents: Agent[];
  categoryLabel: (value: string) => string;
}) {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState('');
  const [customer, setCustomer] = useState<TicketCustomer | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'not_found' | 'error'>('idle');
  const [lookupMessage, setLookupMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const mobile = normalizeMobile(phone);
  const phoneValid = MOBILE_PATTERN.test(mobile);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!phone.trim()) e.phone = 'Customer phone is required';
    else if (!phoneValid) e.phone = 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9';
    else if (!customer) e.customer = 'Click Find to look up and select the customer';
    const s = subject.trim();
    if (!s) e.subject = 'Subject is required';
    else if (s.length < SUBJECT_MIN) e.subject = `Subject must be at least ${SUBJECT_MIN} characters`;
    const d = description.trim();
    if (!d) e.description = 'Description is required';
    else if (d.length < DESCRIPTION_MIN) e.description = `Description must be at least ${DESCRIPTION_MIN} characters`;
    if (!category) e.category = 'Select a category';
    return e;
  }, [phone, phoneValid, customer, subject, description, category]);

  const show = (key: string) => (submitAttempted || touched[key]) && !!errors[key];
  const touch = (key: string) => setTouched((t) => ({ ...t, [key]: true }));

  const lookup = async () => {
    touch('phone');
    if (!phoneValid) return;
    setLookupState('loading');
    setLookupMessage('');
    setCustomer(null);
    try {
      const res = await api.get('/admin/users/lookup', { params: { phone: mobile, userType: 'customer' } });
      const user = res.data.data as TicketCustomer | null;
      if (!user?._id) {
        setLookupState('not_found');
        setLookupMessage(`No customer account is registered with ${mobile}. Check the number or ask the customer to sign up first.`);
        return;
      }
      setCustomer(user);
      setLookupState('idle');
    } catch (err) {
      setLookupState('error');
      setLookupMessage(apiMessage(err, 'Customer lookup failed. Try again.'));
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/helpdesk/tickets/admin-create', {
        userId: customer!._id,
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        assignedTo: assignedTo || undefined,
      });
      return res.data.data as Ticket;
    },
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['helpdesk-stats'] });
      onCreated(ticket);
    },
  });

  const submit = () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length) return;
    createMutation.mutate();
  };

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>(['general']);
    return categories.filter((c) => {
      const key = c.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [categories]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create support ticket</DialogTitle>
      <DialogContent dividers>
        {submitAttempted && Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>Complete these fields to create the ticket:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {Object.values(errors).map((m) => <li key={m}><Typography variant="body2">{m}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {createMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{apiMessage(createMutation.error, 'Could not create the ticket.')}</Alert>
        )}
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Customer phone"
                required
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/[^\d+\s-]/g, '').slice(0, 16));
                  setCustomer(null);
                  setLookupState('idle');
                }}
                onBlur={() => touch('phone')}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
                error={show('phone') || lookupState === 'not_found' || lookupState === 'error'}
                helperText={show('phone')
                  ? errors.phone
                  : '10-digit mobile number of the registered customer, then click Find'}
                inputProps={{ inputMode: 'tel', 'aria-label': 'Customer phone' }}
                InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={lookup}
                disabled={lookupState === 'loading'}
                startIcon={lookupState === 'loading' ? <CircularProgress size={16} /> : <PersonSearch />}
                sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 100 }}
              >
                Find
              </Button>
            </Stack>
            {(lookupState === 'not_found' || lookupState === 'error') && (
              <Alert severity={lookupState === 'not_found' ? 'warning' : 'error'} sx={{ mt: 1 }}>{lookupMessage}</Alert>
            )}
            {customer ? (
              <Paper variant="outlined" sx={{ mt: 1, p: 1.5, borderRadius: 2, borderColor: 'success.main', bgcolor: (t) => alpha(t.palette.success.main, 0.06) }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}><CheckCircle fontSize="small" /></Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={800}>{personName(customer) || 'Unnamed customer'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[customer.phone, customer.email].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                      Customer ID: {customer._id}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => { setCustomer(null); setPhone(''); }}>Change</Button>
                </Stack>
                <Typography variant="caption" color="success.dark" display="block" sx={{ mt: 0.75 }}>
                  This ticket will be linked to this customer.
                </Typography>
              </Paper>
            ) : show('customer') ? (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5, ml: 1.75 }}>{errors.customer}</Typography>
            ) : null}
          </Box>

          <TextField
            label="Subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
            onBlur={() => touch('subject')}
            error={show('subject')}
            helperText={show('subject') ? errors.subject : `${subject.trim().length}/${SUBJECT_MAX} · short summary of the issue`}
            fullWidth
          />
          <TextField
            label="Description"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            onBlur={() => touch('description')}
            error={show('description')}
            helperText={show('description')
              ? errors.description
              : `${description.trim().length}/${DESCRIPTION_MAX} · details agents need: what happened, booking ID, amount, etc.`}
            multiline
            rows={4}
            fullWidth
          />
          <TextField
            select
            label="Category"
            required
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onBlur={() => touch('category')}
            error={show('category')}
            helperText={show('category') ? errors.category : 'What the issue is about. Used for routing and the Category filter.'}
            fullWidth
            {...selectProps}
          >
            <MenuItem value="" disabled>Select a category</MenuItem>
            <MenuItem value="general">General</MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c.name} value={c.name}>{categoryLabel(c.name)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
            helperText="Defaults to Medium. Use High or Urgent for safety, payment or account-access issues."
            fullWidth
          >
            {PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>{PRIORITY_LABEL[p]}{p === 'medium' ? ' (default)' : ''}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Assign to (optional)"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            helperText={agents.length
              ? 'Optional. Leave unassigned to triage later; assigning moves the ticket to In Progress.'
              : 'No active support agents found. Add employee/admin users to assign tickets.'}
            fullWidth
            {...selectProps}
          >
            <MenuItem value="">Unassigned (triage later)</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a._id} value={a._id}>{a.name}{a.userType ? ` · ${titleCase(a.userType)}` : ''}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          <Typography component="span" color="error.main">*</Typography> Required
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating…' : 'Create ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function HelpdeskPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState<TicketStatus | ''>('');
  const [newPriority, setNewPriority] = useState<TicketPriority | ''>('');
  const [newAssignee, setNewAssignee] = useState('');
  const [resolution, setResolution] = useState('');
  const [createKey, setCreateKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdNotice, setCreatedNotice] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: categories } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: async () => {
      const res = await api.post('/complaint/categories', { type: 'user' });
      return res.data.data as Category[];
    },
  });

  const { data: agents } = useQuery({
    queryKey: ['helpdesk-agents'],
    queryFn: async () => {
      const res = await api.get('/helpdesk/agents');
      return res.data.data as Agent[];
    },
  });

  const categoryLabel = (value: string) => {
    if (!value) return '—';
    if (value.toLowerCase() === 'general') return 'General';
    const match = (categories || []).find((c) => c.name.toLowerCase() === value.toLowerCase());
    return match?.complaint || match?.name || titleCase(value);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['helpdesk-tickets', statusFilter, categoryFilter, sourceFilter, assigneeFilter, search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page + 1), limit: String(PAGE_SIZE) });
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (assigneeFilter) params.set('assignedTo', assigneeFilter);
      if (search) params.set('search', search);
      const res = await api.get(`/helpdesk/tickets?${params}`);
      return res.data.data as { tickets: Ticket[]; total: number };
    },
    placeholderData: keepPreviousData,
  });

  const lastTotal = useRef(0);
  if (data?.total != null) lastTotal.current = data.total;
  const totalTickets = data?.total ?? lastTotal.current;

  const loadTicketIntoDialog = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setNewPriority(ticket.priority);
    setNewAssignee(ticket.assignedTo?._id || '');
    setResolution(ticket.resolution || '');
  };

  const refreshSelectedTicket = async (ticketId: string) => {
    const res = await api.get(`/helpdesk/tickets/${ticketId}`);
    loadTicketIntoDialog(res.data.data as Ticket);
  };

  useEffect(() => {
    const ticketId = searchParams.get('ticketId');
    if (!ticketId) return;
    refreshSelectedTicket(ticketId).catch(() => {});
    const next = new URLSearchParams(searchParams);
    next.delete('ticketId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const { data: stats } = useQuery({
    queryKey: ['helpdesk-stats'],
    queryFn: async () => {
      const res = await api.get('/helpdesk/stats');
      return res.data.data as Array<{ _id: TicketStatus; count: number }>;
    },
  });

  const replyMutation = useMutation({
    mutationFn: async ({ id, message }: { id: string; message: string }) => {
      await api.post(`/helpdesk/tickets/${id}/reply`, { message });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['helpdesk-stats'] });
      setReplyText('');
      if (selectedTicket) await refreshSelectedTicket(selectedTicket._id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      id: string;
      status?: TicketStatus;
      priority?: TicketPriority;
      assignedTo: string | null;
      resolution?: string;
    }) => {
      await api.put(`/helpdesk/tickets/${payload.id}`, {
        status: payload.status,
        priority: payload.priority,
        assignedTo: payload.assignedTo,
        resolution: payload.resolution,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['helpdesk-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['helpdesk-stats'] });
      if (selectedTicket) await refreshSelectedTicket(selectedTicket._id);
    },
  });

  const openTicket = (ticket: Ticket) => {
    loadTicketIntoDialog(ticket);
    setReplyText('');
    refreshSelectedTicket(ticket._id).catch(() => {});
  };

  const columns: GridColDef<Ticket>[] = [
    {
      field: 'ticketNumber',
      headerName: 'Ticket #',
      width: 175,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
          <Tooltip title={row.ticketNumber}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
              {row.ticketNumber}
            </Typography>
          </Tooltip>
          <CopyButton value={row.ticketNumber} label="Copy ticket #" />
        </Stack>
      ),
    },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => (
        <Tooltip title={String(value || '')}>
          <Typography variant="body2" noWrap>{String(value || '—')}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      minWidth: 210,
      flex: 0.8,
      valueGetter: (_v, row) => customerLabel(row),
      renderCell: ({ row, value }) => {
        const c = ticketCustomer(row);
        return (
          <Tooltip
            title={c ? (
              <Box>
                <Typography variant="caption" display="block" fontWeight={700}>{personName(c) || 'Unnamed customer'}</Typography>
                {c.phone && <Typography variant="caption" display="block">Phone: {c.phone}</Typography>}
                {c.email && <Typography variant="caption" display="block">Email: {c.email}</Typography>}
                <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>ID: {c._id}</Typography>
              </Box>
            ) : 'Customer account not found'}
          >
            <Typography variant="body2" noWrap color={c ? undefined : 'text.secondary'}>{String(value)}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      valueGetter: (_v, row) => categoryLabel(row.category),
      renderCell: ({ value }) => (
        <Tooltip title={String(value)}><Typography variant="body2" noWrap>{String(value)}</Typography></Tooltip>
      ),
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      width: 160,
      valueGetter: (_v, row) => (row.assignedTo ? personName(row.assignedTo) : 'Unassigned'),
      renderCell: ({ row, value }) => (row.assignedTo
        ? <Tooltip title={String(value)}><Typography variant="body2" noWrap>{String(value)}</Typography></Tooltip>
        : <Chip size="small" variant="outlined" color="warning" label="Unassigned" />),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 105,
      valueGetter: (_v, row) => PRIORITY_LABEL[row.priority] || titleCase(row.priority || ''),
      renderCell: ({ row, value }) => (
        <Chip label={String(value)} size="small" color={PRIORITY_COLOR[row.priority] ?? 'default'} />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 175,
      valueGetter: (_v, row) => STATUS_LABEL[row.status] || titleCase(row.status || ''),
      renderCell: ({ row, value }) => (
        <Chip label={String(value)} size="small" color={STATUS_COLOR[row.status] ?? 'default'} />
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 165,
      valueGetter: (_v, row) => (row.source ? SOURCE_LABEL[row.source] : '—'),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      valueGetter: (_v, row) => fmt(row.createdAt),
      renderCell: ({ row, value }) => (
        <Tooltip title={fmt(row.createdAt, true)}>
          <Typography variant="body2" noWrap>{String(value)}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" onClick={() => openTicket(row)}>View</Button>
      ),
    },
  ];

  const activeFilterCount = [statusFilter, categoryFilter, sourceFilter, assigneeFilter, search].filter(Boolean).length;
  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setSourceFilter('');
    setAssigneeFilter('');
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const selectedCustomer = selectedTicket ? ticketCustomer(selectedTicket) : null;
  const hasPendingUpdate = !!selectedTicket && (
    newStatus !== selectedTicket.status
    || newPriority !== selectedTicket.priority
    || newAssignee !== (selectedTicket.assignedTo?._id || '')
    || resolution.trim() !== (selectedTicket.resolution || '').trim()
  );

  return (
    <PageShell>
      <PageHeader
        title="Helpdesk"
        subtitle="Manage customer support tickets"
        actions={(
          <Button variant="contained" startIcon={<Add />} onClick={() => { setCreateKey((k) => k + 1); setCreateOpen(true); }}>
            Create ticket
          </Button>
        )}
      />

      {createdNotice && (
        <Alert severity="success" onClose={() => setCreatedNotice('')} sx={{ mb: 2 }}>{createdNotice}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {STATUSES.map((s) => {
          const count = (stats ?? []).find((x) => x._id === s)?.count ?? 0;
          const active = statusFilter === s;
          return (
            <Paper
              key={s}
              onClick={() => { setStatusFilter(active ? '' : s); setPage(0); }}
              sx={{
                px: 2.5, py: 1.5, minWidth: 130, textAlign: 'center', cursor: 'pointer',
                border: 2, borderColor: active ? 'primary.main' : 'transparent',
              }}
            >
              <Typography variant="h5" fontWeight={700}>{count}</Typography>
              <Typography variant="caption" color="text.secondary">{STATUS_LABEL[s]}</Typography>
          </Paper>
          );
        })}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Ticket #, subject, customer name or phone"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>,
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}><Close fontSize="small" /></IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ width: 320 }}
        />
        <TextField
          select label="Status" value={statusFilter} size="small" sx={{ width: 200 }}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          {...selectProps}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
        </TextField>
        <TextField
          select label="Source" value={sourceFilter} size="small" sx={{ width: 210 }}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
          {...selectProps}
        >
          <MenuItem value="">All sources</MenuItem>
          {(Object.keys(SOURCE_LABEL) as TicketSource[]).map((s) => <MenuItem key={s} value={s}>{SOURCE_LABEL[s]}</MenuItem>)}
        </TextField>
        <TextField
          select label="Category" value={categoryFilter} size="small" sx={{ width: 220 }}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          {...selectProps}
        >
          <MenuItem value="">All categories</MenuItem>
          <MenuItem value="general">General</MenuItem>
          {[...new Map((categories ?? []).map((c) => [c.name.toLowerCase(), c])).values()]
            .filter((c) => c.name.toLowerCase() !== 'general')
            .map((c) => <MenuItem key={c.name} value={c.name}>{categoryLabel(c.name)}</MenuItem>)}
        </TextField>
        <TextField
          select label="Assignee" value={assigneeFilter} size="small" sx={{ width: 210 }}
          onChange={(e) => { setAssigneeFilter(e.target.value); setPage(0); }}
          {...selectProps}
        >
          <MenuItem value="">All tickets</MenuItem>
          <MenuItem value="unassigned">Unassigned only</MenuItem>
          <MenuItem value="assigned">Assigned to anyone</MenuItem>
          <Divider />
          {(agents ?? []).map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
        </TextField>
        {activeFilterCount > 0 && <Button size="small" onClick={clearFilters}>Clear filters ({activeFilterCount})</Button>}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {totalTickets.toLocaleString()} ticket{totalTickets === 1 ? '' : 's'}{activeFilterCount ? ' match the filters' : ''}
      </Typography>

      <SerialNoDataGrid
        rows={data?.tickets ?? []}
        columns={columns}
        loading={isLoading || isFetching}
        rowCount={totalTickets}
        paginationMode="server"
        paginationModel={{ page, pageSize: PAGE_SIZE }}
        pageSizeOptions={[PAGE_SIZE]}
        onPaginationModelChange={(m) => setPage(m.page)}
        getRowId={(r) => r._id}
        autoHeight
      />

      {createOpen && (
        <CreateTicketDialog
          key={createKey}
          open
          onClose={() => setCreateOpen(false)}
          onCreated={(ticket) => {
            setCreateOpen(false);
            setCreatedNotice(`Ticket ${ticket.ticketNumber} created for ${customerLabel(ticket)}.`);
            openTicket(ticket);
          }}
          categories={categories ?? []}
          agents={agents ?? []}
          categoryLabel={categoryLabel}
        />
      )}

      <Dialog open={!!selectedTicket} onClose={() => setSelectedTicket(null)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight={800} sx={{ fontFamily: 'monospace' }}>{selectedTicket.ticketNumber}</Typography>
                <CopyButton value={selectedTicket.ticketNumber} label="Copy ticket #" />
                <Chip label={STATUS_LABEL[selectedTicket.status]} size="small" color={STATUS_COLOR[selectedTicket.status]} />
                <Chip label={`${PRIORITY_LABEL[selectedTicket.priority]} priority`} size="small" color={PRIORITY_COLOR[selectedTicket.priority]} variant="outlined" />
                {selectedTicket.source && <Chip label={SOURCE_LABEL[selectedTicket.source]} size="small" variant="outlined" />}
              </Stack>
              <Typography variant="subtitle1" sx={{ mt: 0.5 }}>{selectedTicket.subject}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <DetailRow label="Customer">
                    {selectedCustomer ? (
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" fontWeight={700}>{personName(selectedCustomer) || 'Unnamed customer'}</Typography>
                          <Tooltip title="Open customer profile">
                            <IconButton size="small" onClick={() => navigate(`/customers/${selectedCustomer._id}`, { state: { from: '/helpdesk' } })}>
                              <OpenInNew sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[selectedCustomer.phone, selectedCustomer.email].filter(Boolean).join(' · ') || 'No contact details'}
              </Typography>
                      </Box>
                    ) : <Typography variant="body2" color="text.secondary">Customer account not found</Typography>}
                  </DetailRow>
                  <DetailRow label="Category"><Typography variant="body2">{categoryLabel(selectedTicket.category)}</Typography></DetailRow>
                  <DetailRow label="Assigned to">
                {selectedTicket.assignedTo
                      ? <Typography variant="body2">{personName(selectedTicket.assignedTo)}</Typography>
                      : <Chip size="small" variant="outlined" color="warning" label="Unassigned" />}
                  </DetailRow>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailRow label="Created"><Typography variant="body2">{fmt(selectedTicket.createdAt, true)}</Typography></DetailRow>
                  <DetailRow label="SLA deadline"><Typography variant="body2">{fmt(selectedTicket.slaDeadline, true)}</Typography></DetailRow>
                  <DetailRow label="First response"><Typography variant="body2">{fmt(selectedTicket.firstResponseAt, true)}</Typography></DetailRow>
                  {selectedTicket.resolvedAt && (
                    <DetailRow label="Resolved"><Typography variant="body2">{fmt(selectedTicket.resolvedAt, true)}</Typography></DetailRow>
                  )}
                </Grid>
              </Grid>

              {selectedTicket.description && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{selectedTicket.description}</Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>Conversation</Typography>
                  <Stack spacing={1.5} sx={{ maxHeight: 280, overflowY: 'auto', mb: 2 }}>
                    {selectedTicket.messages.map((msg, i) => {
                      const fromAgent = ADMIN_SENDER_TYPES.has(msg.senderType);
                      return (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: fromAgent ? 'primary.50' : 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                            {fromAgent ? 'Support team' : 'Customer'} · {fmt(msg.sentAt, true)}
                    </Typography>
                          <Typography variant="body2" mt={0.5} sx={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Typography>
                  </Paper>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>History</Typography>
                  <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', mb: 2 }}>
                    {(selectedTicket.history ?? []).slice().reverse().map((h, i) => (
                      <Box key={i} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.25 }}>
                        <Typography variant="body2">{historyText(h)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmt(h.at, true)}{h.by?.name ? ` · ${h.by.name}` : h.byType ? ` · ${titleCase(h.byType)}` : ''}
                        </Typography>
                      </Box>
                ))}
              </Stack>
                </Grid>
              </Grid>

              <TextField
                label="Reply to customer"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 2 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  select label="Status" value={newStatus} size="small" sx={{ minWidth: 200 }}
                  onChange={(e) => setNewStatus(e.target.value as TicketStatus)}
                >
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Priority" value={newPriority} size="small" sx={{ minWidth: 160 }}
                  onChange={(e) => setNewPriority(e.target.value as TicketPriority)}
                >
                  {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{PRIORITY_LABEL[p]}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Assign to" value={newAssignee} size="small" sx={{ minWidth: 220 }}
                  onChange={(e) => setNewAssignee(e.target.value)}
                  {...selectProps}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {(agents ?? []).map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                </TextField>
              </Stack>

              <TextField
                label="Resolution notes"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
              {(updateMutation.isError || replyMutation.isError) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {apiMessage(updateMutation.error || replyMutation.error, 'Could not update the ticket.')}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTicket(null)}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => updateMutation.mutate({
                  id: selectedTicket._id,
                  status: newStatus || undefined,
                  priority: newPriority || undefined,
                  assignedTo: newAssignee || null,
                  resolution,
                })}
                disabled={!hasPendingUpdate || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => replyMutation.mutate({ id: selectedTicket._id, message: replyText })}
                disabled={!replyText.trim() || replyMutation.isPending}
              >
                Send Reply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PageShell>
  );
}
